import React, { useMemo, useState } from "react";
import male from "../../../../assets/male.png";
import { TabularView } from "./chatAnalyticsService";

interface ChatConversationsTableProps {
  data: TabularView | null;
}

type ScopeTab = "external" | "internal";

const ChatConversationsTable: React.FC<ChatConversationsTableProps> = ({ data }) => {
  const [scope, setScope] = useState<ScopeTab>("external");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const externalRows = useMemo(
    () => [...(data?.external_chat || [])].sort((a, b) => b.total_messages - a.total_messages),
    [data]
  );
  const internalRows = useMemo(
    () => [...(data?.internal_chat || [])].sort((a, b) => b.total_messages - a.total_messages),
    [data]
  );

  const rows = scope === "external" ? externalRows : internalRows;
  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const paginatedRows = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleScopeChange = (newScope: ScopeTab) => {
    setScope(newScope);
    setCurrentPage(1);
  };

  return (
    <div className="row row-gap-3">
      <div className="col-12 d-flex">
        <div className="card flex-fill mb-4 ca-chart-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center">
              <span className="ca-chart-icon icon-amber">
                <i className="ti ti-table" />
              </span>
              <h5 className="mb-0">Chats &amp; Messages by Participant</h5>
            </div>
            <div className="ca-scope-toggle" role="tablist" aria-label="Chat scope">
              <button
                type="button"
                role="tab"
                aria-selected={scope === "external"}
                className={`ca-scope-toggle-btn ${scope === "external" ? "active" : ""}`}
                onClick={() => handleScopeChange("external")}
              >
                External
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={scope === "internal"}
                className={`ca-scope-toggle-btn ${scope === "internal" ? "active" : ""}`}
                onClick={() => handleScopeChange("internal")}
              >
                Internal
              </button>
            </div>
          </div>
          <div className="card-body">
            {rows.length === 0 ? (
              <div className="text-center py-4">
                <i className="ti ti-message-off fs-1 text-muted mb-2" />
                <p className="text-muted mb-0">
                  No {scope === "external" ? "external" : "internal"} chats in this selection yet
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table ca-agent-table mb-0">
                  <thead>
                    <tr>
                      <th>{scope === "external" ? "End User" : "Participant"}</th>
                      <th className="text-center">Total Messages</th>
                      {scope === "external" ? (
                        <>
                          <th className="text-center">Agent Messages</th>
                          <th className="text-center">End User Messages</th>
                          <th className="text-center">Bot Messages</th>
                        </>
                      ) : (
                        <>
                          <th className="text-center">Sent Messages</th>
                          <th className="text-center">Received Messages</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {scope === "external"
                      ? paginatedRows.map((row, index) => (
                        <tr key={`ext-${row.external_chat_enduser_name}-${index}`}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <img src={male} alt={row.external_chat_enduser_name} className="ca-avatar-img" />
                              {row.external_chat_enduser_name}
                            </div>
                          </td>
                          <td className="text-center num">{row.total_messages}</td>
                          <td className="text-center num">{(row as any).total_agent_messages}</td>
                          <td className="text-center num">{(row as any).total_enduser_messages}</td>
                          <td className="text-center num">{(row as any).total_bot_messages}</td>
                        </tr>
                      ))
                      : paginatedRows.map((row, index) => (
                        <tr key={`int-${row.external_chat_enduser_name}-${index}`}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <img src={male} alt={row.external_chat_enduser_name} className="ca-avatar-img" />
                              {row.external_chat_enduser_name}
                            </div>
                          </td>
                          <td className="text-center num">{row.total_messages}</td>
                          <td className="text-center num">{(row as any).total_sent_messages}</td>
                          <td className="text-center num">{(row as any).total_received_messages}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                <span className="text-muted fs-14">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
                </span>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    style={{ borderRadius: '10px' }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    <span style={{ marginBottom: 5 }}>{`< Previous`}</span>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={currentPage === totalPages}
                    style={{ borderRadius: '10px' }}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  >
                    <span style={{ marginBottom: 5 }}>{`Next >`}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatConversationsTable;
